import { Bounds, Coordinate, Dimensions, MapSpriteCap, TileSize } from "game-common/models"
import { getCurrentTime } from "game-common/time_util"
import { randomCoordinateWithinBounds, randomId, randomOneOf } from "game-common/util"

import {
    BaseMapBuilder,
    FeatureMeta,
    FeaturePointMeta,
    FeatureSpec,
    FragmentSpriteMeta,
    MapEntry,
    RoomMeta,
} from "game-common/room/base_map_builder"
import { FeatureCategory, FeatureSubCategory } from "game-common/room/feature_catalog"

export interface FeaturesDescriptor {
    catalog?: string
    category?: FeatureCategory
    subCategory?: FeatureSubCategory
    originBiomeName?: string
}

export class BaseFeatureBuilder {
    tiledMapBuilder: BaseMapBuilder
    featureSpec: FeatureSpec
    roomId: string
    featureType: string
    originBiomeName: string
    finalFeatureRegionBounds: Bounds
    boundsToClear: Bounds[] = []
    featureContext: any
    clear: boolean = true
    featureId: string

    constructor(tiledMapBuilder: BaseMapBuilder, featureType: string) {
        this.tiledMapBuilder = tiledMapBuilder
        this.featureType = featureType
        this.featureId = randomId()
    }

    seekSpawnBounds = (roomBounds: Bounds, dimensions: Dimensions, targetBiomes: any[]): Bounds => {
        const start = getCurrentTime()
        while (getCurrentTime() - start < 1500) {
            const target = this.tiledMapBuilder.randomOneOf(targetBiomes)
            const targetBounds = new Bounds(target.x, target.y, target.x + target.width, target.y + target.height)
            const candidateCoordinates = this.tiledMapBuilder.randomCoordinateWithinBounds(targetBounds)

            const boundsToCheck = new Bounds(
                candidateCoordinates.x,
                candidateCoordinates.y,
                candidateCoordinates.x + dimensions.w * TileSize,
                candidateCoordinates.y + dimensions.h * TileSize,
            )

            if (roomBounds.encloses(boundsToCheck)) {
                return boundsToCheck
            }
        }

        return null
    }

    build = (
        roomId: string,
        dimensions?: Dimensions,
        featuresDescriptor?: FeaturesDescriptor,
        roomCoordinates?: Coordinate,
    ) => {
        this.featureSpec = {
            dimensions,
            mapEntries: [],
            fragments: [],
        }
    }

    print = (spawnLocation: Coordinate, roomMeta?: Map<string, RoomMeta>): FeatureMeta => {
        console.log(this.featureType, "at", this.roomId, spawnLocation)

        const mapSpriteCaps: MapSpriteCap[] = []
        const featureSprites: FragmentSpriteMeta[] = []
        const featurePoints: FeaturePointMeta[] = []
        const postFeatureMapEntries: MapEntry[] = []
        const { mapX, mapY } = this.tiledMapBuilder.maps[this.roomId]

        const toClear: MapEntry[] = []
        const layers = ["map2", "map3", "map4", "map5", "map6", "sprite", "sprite2"]

        if (this.clear) {
            this.boundsToClear.forEach(bounds => {
                for (let x = bounds.x1; x < bounds.x2; x++) {
                    for (let y = bounds.y1; y < bounds.y2; y++) {
                        const gX = mapX * this.tiledMapBuilder.mapSize + spawnLocation.x + x
                        const gY = mapY * this.tiledMapBuilder.mapSize + spawnLocation.y + y

                        layers.forEach(layerId => {
                            toClear.push({
                                mapId: this.roomId,
                                layerId,
                                tileSpec: {
                                    tilesetId: "lpc-terrain",
                                    tilePos: 722,
                                },
                                x: gX,
                                y: gY,
                            })
                        })
                    }
                }
            })

            this.tiledMapBuilder.applyEntries(toClear)
        }

        ////
        ////
        ////

        const entries = this.featureSpec.mapEntries
            .map((entry: MapEntry) => {
                const { x: cX, y: cY } = entry
                const x = spawnLocation.x + this.featureSpec.dimensions.w / 2
                const y = spawnLocation.y + this.featureSpec.dimensions.h / 2
                const gX = mapX * this.tiledMapBuilder.mapSize + x + cX
                const gY = mapY * this.tiledMapBuilder.mapSize + y + cY

                if (this.originBiomeName && !this.tiledMapBuilder.scanPoint(this.originBiomeName, gX, gY)) {
                    return
                }

                return {
                    ...entry,
                    x: gX,
                    y: gY,
                }
            })
            .filter(n => !!n)

        this.featureSpec?.postFeatureEntries
            ?.map((entry: MapEntry) => {
                const { x: cX, y: cY } = entry
                const x = spawnLocation.x + this.featureSpec.dimensions.w / 2
                const y = spawnLocation.y + this.featureSpec.dimensions.h / 2
                const gX = mapX * this.tiledMapBuilder.mapSize + x + cX
                const gY = mapY * this.tiledMapBuilder.mapSize + y + cY

                if (this.originBiomeName && !this.tiledMapBuilder.scanPoint(this.originBiomeName, gX, gY)) {
                    return
                }

                return {
                    ...entry,
                    x: gX,
                    y: gY,
                }
            })
            .filter(n => !!n)
            .forEach(n => postFeatureMapEntries.push(n))

        this.tiledMapBuilder.applyEntries(entries, roomMeta)
        entries
            .filter(entry => !!entry.mapSpriteCap)
            .forEach(entry => {
                mapSpriteCaps.push({
                    x: entry.x * TileSize + TileSize / 2,
                    y: entry.y * TileSize + TileSize / 2,
                    context: entry.mapSpriteCap,
                })
            })

        ////
        ////
        ////
        const previousFeatureBounds: Bounds[] = []
        const delayed = []
        this.featureSpec.fragments.forEach(featureFragment => {
            const featureLocation = {
                x: spawnLocation.x + featureFragment.location.x,
                y: spawnLocation.y + featureFragment.location.y,
            }
            previousFeatureBounds.push(
                new Bounds(
                    featureLocation.x,
                    featureLocation.y,
                    featureLocation.x + featureFragment.dimensions.w,
                    featureLocation.y + featureFragment.dimensions.h,
                ),
            )
            delayed.push(() => {
                this.tiledMapBuilder.printFeature(
                    featureFragment,
                    featureLocation,
                    mapX,
                    mapY,
                    this.roomId,
                    featureSprites,
                    featurePoints,
                    postFeatureMapEntries,
                    mapSpriteCaps,
                )
            })
        })

        delayed.forEach(d => d())

        const finalFeatureRegionBounds = new Bounds(-1, -1, -1, -1)
        finalFeatureRegionBounds.widen(previousFeatureBounds)

        const bounds = Bounds.from(
            Math.max(0, finalFeatureRegionBounds.x1 * TileSize),
            Math.max(0, finalFeatureRegionBounds.y1 * TileSize),
            Math.min(this.tiledMapBuilder.mapSize * TileSize, finalFeatureRegionBounds.width * TileSize),
            Math.min(this.tiledMapBuilder.mapSize * TileSize, finalFeatureRegionBounds.height * TileSize),
        )
        const featureRegion = this.tiledMapBuilder.toFeatureRegion(
            bounds.x1,
            bounds.y1,
            bounds.width,
            bounds.height,
            this.featureType,
            this.featureContext,
        )

        this.finalFeatureRegionBounds = finalFeatureRegionBounds

        const debug: any[] = (this.featureSpec.debug || []).map(next => {
            return this.tiledMapBuilder.toDebugObject(next.text, next.x, next.y)
        })

        return {
            type: this.featureType,
            roomId: this.roomId,
            spawnLocation,
            region: featureRegion,
            bounds,
            featureSpriteMeta: featureSprites.map(next => this.tiledMapBuilder.toFeatureSpriteRegion(next)),
            subFeaturesMeta: featureSprites.map(next => next.subFeatures).flat(),
            subFeaturesRegions: mapSpriteCaps.map(n => {
                return this.tiledMapBuilder.toMapSpriteCap(n.x, n.y, n.context)
            }),
            featurePoints,
            postFeatureMapEntries,
            debug,
        }
    }
}
